import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { App } from './app/App';
import { setRuntimeSocketUrl } from './lib/socket';
import { isDevEnv } from './lib/env';
import './index.css';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Dev environment visual markers: " - DEV" title suffix + a fixed green
 * 3px border around the viewport so it's obvious you're not on production.
 */
function applyDevEnvMarkers(): void {
  if (!isDevEnv()) return;

  if (!/-\s*DEV$/.test(document.title)) {
    document.title = `${document.title} - DEV`;
  }

  const border = document.createElement('div');
  border.setAttribute('data-dev-border', '');
  border.style.cssText = [
    'position:fixed',
    'inset:0',
    'border:3px solid #16a34a',
    'pointer-events:none',
    'z-index:2147483647',
  ].join(';');
  document.body.appendChild(border);
}

/**
 * Fetch deploy-specific runtime config (socket URL) from the backend before
 * the app mounts, so these values live in the DB feature flags instead of
 * build-time env vars. Failure is non-fatal: socket resolution falls back to
 * the build-time env / same-origin chain.
 */
async function loadRuntimeConfig(): Promise<void> {
  try {
    const res = await fetch(`${API_URL}/settings/public-config`);
    if (!res.ok) return;
    const json = await res.json();
    setRuntimeSocketUrl(json?.data?.socketUrl);
  } catch {
    // ignore — fall back to env-based resolution
  }
}

/**
 * Blocca il pinch-zoom su mobile per un'esperienza app-like a tutto schermo,
 * senza il bordo bianco che comparirebbe zoomando oltre il contenuto.
 * Il meta viewport (user-scalable=no) copre Android; iOS Safari lo ignora,
 * quindi annulliamo esplicitamente i gesture di zoom. Lo scroll normale a un
 * dito resta invariato.
 */
function preventPinchZoom(): void {
  const stop = (e: Event) => e.preventDefault();
  ['gesturestart', 'gesturechange', 'gestureend'].forEach((evt) =>
    document.addEventListener(evt, stop, { passive: false })
  );
}

applyDevEnvMarkers();
preventPinchZoom();

void loadRuntimeConfig().finally(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
});
